import React, { Component, forwardRef } from 'react';
import { Grid, Select, MenuItem, TextField } from '@material-ui/core';
import MaterialTable from '@material-table/core';
import { AddBox, ArrowDownward, Check, ChevronLeft, ChevronRight, Clear, DeleteOutline, Edit, FilterList, FirstPage, Language, LastPage, Remove, SaveAlt, Search, ViewColumn } from '@material-ui/icons';
import languageLookup from './availableLanguages';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  Language: forwardRef((props, ref) => <Language {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />)
};

const defaultLanguage = {
  outlook: {
      quarterly: "Quarterly",
      estimate: "Estimate",
      viewDetails: "View Details",
      policySuccessMessage: "This meeting follows the meeting policy guidance {RULES}.",
      policyErrorMessage: "This meeting has errors {RULES}. Open the 'Meeting Culture' Add-in for details. ⚠️",
      appointmentTimeout: "",
      estimateTitle: "Meeting Cost Estimates",
      slowConnection: "Configuration update took long. Some of the features might not work as anticipated. Please check your connection, try again later, or contact your account manager. Load time: ",
      popup: {
          cancel: "Show Meeting Policy",
          low: "This meeting is not too expensive 🟩\n\nDo you want to review company meeting policy prior to sending it?",
          medium: "This meeting might be considered costly 🟨\n\nMaybe you should review our meeting policy prior to sending the invites?",
          high: "This meeting is considered extremely costly 🟥\n\nPlease review the meeting policy and plan your event accordingly.",
          error: "❌",
          ruleTitle: "We suggest you review following meeting best practices:"
      }
  },
  google: {
    rule: {
      pass: "✅",
      fail: "❌"
    },
    free: "Flowtrace: Free - Upgrade for custom configuration and rules",
    rulesNotExecuted: "No rules executed: meeting duration is more than 8 hours.",
    rulesHeader :"{failingRules} failing and {passingRules} passing",
    rulesPassOnlyHeader: "{passingRules} rules passing",
    rulesPassingHeader: "Show {passingRules} passing rules",
    validationTitle: "Are you sure you want to send the meeting invite?",
    costTitle: "This meeting can be considered expensive:",
    ruleTitle: "We suggest you review following meeting best practices:",
    ruleMandatoryExplain:"This rule is marked as mandatory.",
    ruleMandatory:"❗",
    reviewCancel: "Review invite",
    reviewSend: "Proceed",
    explain: {
      explainer: "Estimate calculation is based on {invited} participants, {duration} minute meeting, and buffer time of {buffer} minutes per invitee.",
      external: "We identified {externals} external participant(s) whose email address are not known by Flowtrace.",
      rateExplainTitle: "This is the configured rate average breakdown:",
      rateExplain: "{rateExplain} attendee(s)",
      defaultRate: "All invitee cost(s) are based on average of {defaultRate} {currency}.",
      rules: "Show rule explanations:",
      fail: "Notification shown because:",
      pass: "Notification is not shown because:",
      configExplain: "Configuration Used: {source} {age}."
    },
    ruleFail: "❌",
    rulePass: "✅",
    tooltip: {
      login: "Expand for cost explanation, account info, and login options.",
      loginRequired: "Please sign in using the Flowtrace logo on the right. Your organization requires secure connection between Flowtrace and Google Calendar to work."
    },
    quarterly: "Quarterly",
    estimate: "Estimate"
  }
}

const defaultKeyAndValue = [];
const defaultLookupFields = {};
function getDefaults(obj, prefix = '') {
  for (const key in obj) {
      const newPrefix = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        getDefaults(obj[key], newPrefix);
      } else {
        defaultKeyAndValue.push({ [newPrefix]: obj[key] });
        defaultLookupFields[newPrefix] = newPrefix;
      }
  }
  return;
}

getDefaults(defaultLanguage);

function getLanguageTranslations(obj, prefix = '') {
  let result = [];
  for (const key in obj) {
      const newPrefix = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null) {
          result = result.concat(getLanguageTranslations(obj[key], newPrefix));
      } else {
          result.push({ [newPrefix]: obj[key] });
      }
  }
  return result;
}

function setNestedObjectValue(obj, path, value) {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current)) {
      current[key] = {};
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
  return obj;
}

function removeNestedObjectValue(obj, path) {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current)) {
      return obj; // Path doesn't exist, return unchanged
    }
    current = current[key];
  }
  const lastKey = keys[keys.length - 1];
  if (lastKey in current) {
    delete current[lastKey];
  }
  return obj;
}
const existsInNestedObject = (object, path) => {
  const keys = path.split('.');
  let current = object;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current)) {
      return false;
    }
    current = current[key];
  }
  const lastKey = keys[keys.length - 1];
  return lastKey in current;
}


class AppSettingsTranslation extends Component {
  constructor(props) {
    super(props);
    this.state = {initialEditValue: "Select Field"};
  }
  getCurrentLanguageData = () => {
    const settings = this.props.settings;
    const currentTranslations = getLanguageTranslations(settings.language);
    const languageTypedTranslations = currentTranslations.map((translation, index) => {
      const field = Object.keys(translation)[0];
      if(field.startsWith("i18.")){
        const language = field.split('.')[1];
        const languageCode = "i18."+ language;
        return { language, languageCode, field: field.split(languageCode + ".")[1], text: translation[field], index }
      }
      return { language: "default", languageCode: "default", field, text: translation[field], index};
    });
    return languageTypedTranslations;
  }
  isTranslationValid = (translation) => translation.field && translation.language && translation.field.length && translation.language.length
  updateTranslation = (translation, previous) => {
    return new Promise((resolve, reject) => {
      if(!this.isTranslationValid) reject("Translation is missing some information.");
      this.deleteTranslation(previous);
      this.addTranslation(translation);
      console.log("update", translation)
      resolve()
    });
  }
  addTranslation = (translation) => {
    return new Promise((resolve, reject) => {
      if(!this.isTranslationValid) reject("Translation is missing some information.");
      const objectKey = translation.language === "default" ? translation.field : "i18."+ translation.language +"."+ translation.field;
      const currentLanguage = this.props.settings.language ? this.props.settings.language : {};
      if(existsInNestedObject(currentLanguage, objectKey)) reject("Translation already exists.");
      const updatedLanguage = setNestedObjectValue(currentLanguage, objectKey, translation.text);
      this.props.updateTranslation(updatedLanguage);
      console.log("add", translation)
      resolve()
    });
    
  }
  deleteTranslation = (translation) => {
    return new Promise((resolve) => {
      const objectKey = translation.language === "default" ? translation.field : "i18."+ translation.language +"."+ translation.field;
      const currentLanguage = this.props.settings.language ? this.props.settings.language : {};
      const updatedLanguage = removeNestedObjectValue(currentLanguage, objectKey);
      this.props.updateTranslation(updatedLanguage);
      console.log("delete", translation)
      resolve()
    });
  }
  generalSettings = () => {

    const DropDown = ({ value, onChange }) => (
      <Select onChange={onChange} value={value || ""}>
        { defaultKeyAndValue.map((item, index) => <MenuItem key={index} value={Object.keys(item)[0]}>{Object.keys(item)[0]}</MenuItem>)}
      </Select>
    );

    const table = <MaterialTable 
      columns={[
        { title: "Language", field:"language", align:"left", width:0, lookup: languageLookup, defaultGroupOrder: 0, grouping:true },
        { title: "Field", field:"field", defaultSort:"asc", align:"left", width:250, lookup: defaultLookupFields, validate: row => row.field && row.field.length ? true : false, editComponent: props => (
          <DropDown
            value={props.value}
            onChange={e => {
              const newField = e.target.value;
              props.rowData.text = defaultKeyAndValue.find(v => v[newField] !== undefined)[newField];
              props.onChange(newField);
            }}
          />
        )},
        { title: "Language", field:"language", align:"left", width:100, lookup: languageLookup, initialEditValue:"default"},
        { title: "Text", field:"text", initialEditValue:"Select field to see current default text example in english.", editComponent: props => (
          <TextField multiline fullWidth maxRows={6} value={props.value} onChange={e => { props.onChange(e.target.value); }} />
        )},


      ]}
      data={ this.getCurrentLanguageData() }
      icons={tableIcons}
      options={
        {
          addRowPosition: "first",
          showTitle: false,
          emptyRowsWhenPaging: false,
          pageSize:5,
          pageSizeOptions:[5,10,25],
          actionsColumnIndex: -1,
          fixedColumns: { left: 1 }
        }
      }
      editable={
        {
          onRowAdd: this.props.settings.enabled ? this.addTranslation : undefined,
          onRowUpdate: this.props.settings.enabled ? this.updateTranslation : undefined,
          onRowDelete: this.props.settings.enabled ? this.deleteTranslation : undefined,
        }
      }
      />

    const generalSettings =
      <Grid item xs={12}>
          { this.props.createHeader("Meeting Cost App Translations", "You can define the default language of the apps and add language specific versions of the app here. The language translation is done based on this order: authenticated user's language, user's browser locale, and falling back to default values.", " ")}          
          {table}
      </Grid>;
      return generalSettings;
  }
  render() {
    if (this.props.loading)
      return this.props.loading();
    return this.generalSettings();
  }
}

export default AppSettingsTranslation;
